import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MEASURE_BY_LABEL, type MeasureBy } from '@obraplan/core/domain/takeoff/geometry';
import {
  acceptsSideMargin,
  APPLIES_TO_LABEL,
  isAreaMeasure,
  isValidPair,
  measureLabel,
  measuresFor,
  SELECTABLE_ELEMENTS,
  serviceElement,
  type AppliesTo,
} from '@obraplan/core/domain/takeoff/applies-to';
import type { Service } from '@obraplan/core/domain/takeoff/service';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { primeiraMaiuscula } from './formato';

export interface ServiceFormSubmit {
  readonly disciplineId: string;
  readonly name: string;
  readonly code: string | undefined;
  readonly appliesTo: AppliesTo;
  readonly measureBy: MeasureBy;
  readonly layerThickness: number | undefined;
  /** Metros para cada lado do vão (RN-308). Só vai na largura de porta, janela e passagem. */
  readonly sideMargin: number | undefined;
  readonly unitOfMeasureId: string | undefined;
}

/**
 * A checklist que inclui o serviço, como o formulário a mostra (RN-297).
 *
 * Só leitura: o vínculo mora na ficha (`serviceIds`, `0023`) e se troca na tela da checklist.
 */
export interface ServiceChecklistRef {
  readonly id: string;
  readonly name: string;
  readonly code?: string | undefined;
  readonly isActive: boolean;
}

/** Onde mora a tela das checklists — para onde os links daqui apontam. */
export const CHECKLISTS_PATH = '/configuracoes/checklists';

/** A mesma classe do `<select>` das outras telas — o componente nativo, com a altura do toque. */
const SELECT_CLASS =
  'h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-2 text-sm';

/**
 * Cadastrar ou corrigir um serviço do catálogo global (Fase 21, fatia 1).
 *
 * O formulário é um só para os dois casos porque os campos são os mesmos — o que muda é o
 * ponto de partida. E o que ele pede é justamente o que o plano chama de **método
 * construtivo**: a disciplina dona, como o serviço mede, a espessura quando ela vira volume
 * e a unidade em que o número é lido (ADR-134, RN-232).
 *
 * ## Por que a medida e a unidade são dois campos, e não um
 *
 * `measureBy` diz **de onde** o número sai da geometria — área de parede contada uma vez,
 * área de face contada dos dois lados, perímetro, volume. A unidade diz **em que** ele é
 * lido por quem confere. "M²" sozinho não distingue alvenaria de reboco, e o palpite errado
 * erra o quantitativo sem que nenhum número pareça estranho.
 *
 * ## E a peça vem antes da medida (Fase 24)
 *
 * "Aplica a" é o elemento — parede, ambiente, pilar, viga — e decide quais medidas existem
 * (RN-256). Trocar para uma peça que não aceita a medida escolhida **limpa** a medida em vez
 * de gravar o par inválido, e o botão espera a nova (RN-260). O serviço antigo de volume sem
 * peça abre com "Aplica a" em branco: é o pendente, e ele não mede nada até a escolha.
 *
 * A espessura só aparece medindo área: é ela que transforma m² em m³, e a mesma parede leva
 * chapisco de 5 mm, emboço de 2 cm e gesso de 5 mm — três volumes de argamassa do mesmo
 * metro quadrado. Em branco não é zero: é serviço que não mede em m³ nenhum.
 *
 * A margem só aparece na largura de porta, janela e passagem (RN-308): é quanto a soleira e o
 * peitoril passam do vão, para cada lado. Em branco é zero — a pedra rente ao vão.
 *
 * ## A checklist ligada ao serviço, só para ler (RN-297)
 *
 * Ao editar, o formulário diz **qual ficha** inclui o serviço — nome e código, com link para
 * abri-la em Configurações → Checklists — ou "Sem checklist", com link para a tela das
 * fichas. Quem edita o serviço precisa saber o que a medição vai perguntar dele, mas o
 * vínculo é da ficha: a lista de serviços mora lá (`serviceIds`), e trocá-lo daqui seria
 * duas telas escrevendo a mesma coisa.
 */
export function ServiceFormDialog({
  open,
  onOpenChange,
  service,
  disciplines,
  units,
  checklists,
  canOpenChecklists = true,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ausente = serviço novo. */
  service: Service | undefined;
  disciplines: readonly { id: string; name: string }[];
  units: readonly { id: string; name: string; code: string }[];
  /**
   * As fichas que incluem este serviço. Ausente = a leitura não voltou, ou quem edita não
   * pode lê-las (`structure.view`): a seção some, em vez de afirmar "Sem checklist" sobre o
   * que não se sabe.
   */
  checklists?: readonly ServiceChecklistRef[] | undefined;
  /** O perfil abre a tela das checklists? Sem a tela, os nomes ficam sem link. */
  canOpenChecklists?: boolean;
  onSubmit: (input: ServiceFormSubmit) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const isEditing = service !== undefined;
  const [disciplineId, setDisciplineId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  /** Vazio é o pendente — volume ou espala sem peça, esperando a escolha. */
  const [appliesTo, setAppliesTo] = useState<AppliesTo | ''>('PAREDE');
  /** Vazio é a medida que a troca de peça limpou, esperando a nova. */
  const [measureBy, setMeasureBy] = useState<MeasureBy | ''>('WALL');
  const [thickness, setThickness] = useState('');
  const [margin, setMargin] = useState('');
  const [unitId, setUnitId] = useState('');

  // Remontado a cada abertura para não herdar o que foi digitado da vez anterior — nem o
  // serviço anterior, quando a tela abre um depois do outro.
  useEffect(() => {
    if (!open) return;
    setDisciplineId(service?.disciplineId ?? disciplines[0]?.id ?? '');
    setName(service?.name ?? '');
    setCode(service?.code ?? '');
    setAppliesTo(service === undefined ? 'PAREDE' : (serviceElement(service) ?? ''));
    setMeasureBy(service?.measureBy ?? 'WALL');
    setThickness(service?.layerThickness === undefined ? '' : String(service.layerThickness));
    setMargin(service?.sideMargin === undefined ? '' : String(service.sideMargin));
    setUnitId(service?.unitOfMeasureId ?? '');
  }, [open, service, disciplines]);

  const escolherElemento = (escolhido: AppliesTo): void => {
    setAppliesTo(escolhido);
    // A medida que a peça nova não aceita sai, e não vira par inválido (RN-256).
    if (measureBy !== '' && !isValidPair(escolhido, measureBy)) setMeasureBy('');
  };
  const medidas = appliesTo === '' ? (measureBy === '' ? [] : [measureBy]) : measuresFor(appliesTo);

  // A camada só vira volume sobre uma área: contagem, comprimento e volume não têm espessura.
  const espessuraFazSentido = measureBy !== '' && isAreaMeasure(measureBy);
  const espessura = Number(thickness.replace(',', '.'));
  const espessuraInvalida =
    thickness.trim() !== '' && (!Number.isFinite(espessura) || espessura <= 0);

  // A margem só existe onde a pedra corre ao longo do vão: a largura de porta, janela e
  // passagem (RN-308). Fora dela o campo some, e o que estava digitado não vai.
  const margemFazSentido =
    appliesTo !== '' && measureBy !== '' && acceptsSideMargin(appliesTo, measureBy);
  const margem = Number(margin.replace(',', '.'));
  const margemInvalida =
    margemFazSentido && margin.trim() !== '' && (!Number.isFinite(margem) || margem < 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar serviço' : 'Novo serviço'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'A mudança vale para todas as obras, daqui para frente. O que já foi medido e verificado continua como está — descreve o que aconteceu.'
              : 'Fica disponível em todas as obras, agora e nas que vierem depois.'}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (appliesTo === '' || measureBy === '') return;
            onSubmit({
              disciplineId,
              name,
              code: code.trim() === '' ? undefined : code.trim(),
              appliesTo,
              measureBy,
              layerThickness:
                espessuraFazSentido && thickness.trim() !== '' && !espessuraInvalida
                  ? espessura
                  : undefined,
              sideMargin:
                margemFazSentido && margin.trim() !== '' && !margemInvalida ? margem : undefined,
              unitOfMeasureId: unitId === '' ? undefined : unitId,
            });
          }}
        >
          <Field
            label="Disciplina"
            htmlFor="service-form-discipline"
            required
            hint="O serviço pertence a uma disciplina, e é por ela que ele aparece na obra."
          >
            <select
              id="service-form-discipline"
              className={SELECT_CLASS}
              value={disciplineId}
              onChange={(event) => setDisciplineId(event.target.value)}
            >
              {disciplines.map((discipline) => (
                <option key={discipline.id} value={discipline.id}>
                  {discipline.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Nome do serviço" htmlFor="service-form-name" required error={errorMessage}>
            <Input
              id="service-form-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="ALVENARIA NÃO ESTRUTURAL"
            />
          </Field>

          <Field
            label="Aplica a"
            htmlFor="service-form-applies-to"
            required
            hint="A peça de onde o número sai. Pilar e viga são escolhas separadas: o volume de um não leva o do outro."
            {...(appliesTo === ''
              ? { error: 'Escolha o elemento — sem ele, o serviço não mede nada.' }
              : {})}
          >
            <select
              id="service-form-applies-to"
              className={SELECT_CLASS}
              value={appliesTo}
              onChange={(event) => escolherElemento(event.target.value as AppliesTo)}
            >
              {appliesTo === '' && (
                <option value="" disabled>
                  Escolha o elemento
                </option>
              )}
              {SELECTABLE_ELEMENTS.map((elemento) => (
                <option key={elemento} value={elemento}>
                  {APPLIES_TO_LABEL[elemento]}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Como mede"
            htmlFor="service-form-measure"
            required
            hint="De onde o número sai do traçado. Assentamento conta a parede uma vez; reboco conta as duas faces."
          >
            <select
              id="service-form-measure"
              className={SELECT_CLASS}
              value={measureBy}
              disabled={appliesTo === ''}
              onChange={(event) => setMeasureBy(event.target.value as MeasureBy)}
            >
              {measureBy === '' && (
                <option value="" disabled>
                  Escolha a medida
                </option>
              )}
              {/* O texto do par, com a conta e a unidade — "faces (4 × altura), m²" (fatia 10). */}
              {medidas.map((medida) => (
                <option key={medida} value={medida}>
                  {appliesTo === ''
                    ? MEASURE_BY_LABEL[medida]
                    : primeiraMaiuscula(measureLabel(appliesTo, medida))}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Unidade de medição"
            htmlFor="service-form-unit"
            hint="Em que o número é lido por quem confere. Sem unidade, ninguém mede este serviço."
          >
            <select
              id="service-form-unit"
              className={SELECT_CLASS}
              value={unitId}
              onChange={(event) => setUnitId(event.target.value)}
            >
              <option value="">Ainda sem unidade</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name} ({unit.code})
                </option>
              ))}
            </select>
          </Field>

          {espessuraFazSentido && (
            <Field
              label="Espessura da camada (m)"
              htmlFor="service-form-thickness"
              hint="Só para quem vira volume: 0,02 para emboço de 2 cm. Em branco, o serviço não mede em m³."
              {...(espessuraInvalida ? { error: 'A espessura precisa ser maior que zero.' } : {})}
            >
              <Input
                id="service-form-thickness"
                inputMode="decimal"
                value={thickness}
                onChange={(event) => setThickness(event.target.value)}
                placeholder="0,02"
              />
            </Field>
          )}

          {margemFazSentido && (
            <Field
              label="Margem para cada lado (m)"
              htmlFor="service-form-side-margin"
              hint="Quanto a soleira ou o peitoril passa do vão, de cada lado: 0,05 soma 10 cm a cada vão. Em branco, a pedra fica rente ao vão."
              {...(margemInvalida ? { error: 'A margem não pode ser negativa.' } : {})}
            >
              <Input
                id="service-form-side-margin"
                inputMode="decimal"
                value={margin}
                onChange={(event) => setMargin(event.target.value)}
                placeholder="0,05"
              />
            </Field>
          )}

          <Field
            label="Código"
            htmlFor="service-form-code"
            hint="Opcional — o código usado nas planilhas da equipe."
          >
            <Input
              id="service-form-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>

          {isEditing && checklists !== undefined && (
            <section aria-label="Checklist de qualidade" className="flex flex-col gap-1.5">
              <h3 className="text-sm font-medium">Checklist de qualidade</h3>
              {checklists.length === 0 ? (
                <p className="text-sm text-(--color-content-muted)">
                  Sem checklist.{' '}
                  {canOpenChecklists && (
                    <Link
                      to={CHECKLISTS_PATH}
                      className="underline underline-offset-2"
                      onClick={() => onOpenChange(false)}
                    >
                      Abrir as checklists
                    </Link>
                  )}
                </p>
              ) : (
                <ul className="flex flex-col gap-0.5 text-sm">
                  {checklists.map((checklist) => (
                    <li key={checklist.id}>
                      {canOpenChecklists ? (
                        <Link
                          to={`${CHECKLISTS_PATH}?ficha=${checklist.id}`}
                          className="underline underline-offset-2"
                          onClick={() => onOpenChange(false)}
                        >
                          {checklist.code === undefined
                            ? checklist.name
                            : `${checklist.code} · ${checklist.name}`}
                        </Link>
                      ) : checklist.code === undefined ? (
                        checklist.name
                      ) : (
                        `${checklist.code} · ${checklist.name}`
                      )}
                      {!checklist.isActive && (
                        <span className="text-(--color-content-muted)"> (desativada)</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-(--color-content-muted)">
                O vínculo se troca na tela da checklist, em &ldquo;Aplica-se a&rdquo;.
              </p>
            </section>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                isPending ||
                name.trim() === '' ||
                disciplineId === '' ||
                appliesTo === '' ||
                measureBy === '' ||
                espessuraInvalida ||
                margemInvalida
              }
            >
              {isPending ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Criar serviço'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
